/**
 * Modern Cart Drawer with Tailwind CSS - Pure Liquid
 * Handles cart drawer functionality including:
 * - Opening/closing drawer
 * - Adding items to cart
 * - Updating quantities
 * - Removing items
 * - Cart totals and checkout
 */

/** LoyaltyLion: re-bind SDK UI after dynamic cart / drawer DOM updates (@see https://developers.loyaltylion.com/sdk/live-updating) */
function refreshLoyaltyLionCartUI() {
  try {
    var lion = window.loyaltylion;
    if (!lion || !lion.ui || typeof lion.ui.refresh !== 'function') {
      return;
    }
    lion.ui.refresh();
  } catch (err) {
    console.warn('LoyaltyLion UI refresh failed:', err);
  }
}

document.addEventListener('cart:updated', function () {
  refreshLoyaltyLionCartUI();
});

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('CartDrawer');
    this.overlay = document.getElementById('cart-overlay');
    this.panel = document.getElementById('cart-panel');
    this.container = document.getElementById('CartContainer');
    this.footer = document.getElementById('cart-footer');
    this.closeButton = document.getElementById('close-cart-drawer');

    this.isOpen = false;
    this.isLoading = false;

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Close drawer events
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Listen for add to cart events
    document.addEventListener('cart:item-added', (e) => {
      this.handleItemAdded(e.detail);
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      this.updateCartDisplay(e.detail);
    });
  }

  async open() {
    if (this.isOpen) return;

    this.isOpen = true;

    // Ensure initial closed state
    this.overlay.classList.add('opacity-0');
    this.overlay.classList.remove('opacity-100');
    this.panel.style.translate = '100% 0';

    // Enable pointer events
    this.drawer.classList.remove('pointer-events-none');
    this.drawer.classList.add('pointer-events-auto');

    // Force reflow
    void this.panel.offsetHeight;

    // Use setTimeout to ensure CSS transition triggers
    setTimeout(() => {
      this.overlay.classList.remove('opacity-0');
      this.overlay.classList.add('opacity-100');
      this.panel.style.translate = '0 0';
    }, 10);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Trigger animations
    this.overlay.classList.remove('opacity-100');
    this.overlay.classList.add('opacity-0');
    this.panel.style.translate = '100% 0';

    // Allow body scroll
    document.body.style.overflow = '';

    // Disable pointer events after animation
    setTimeout(() => {
      if (!this.isOpen) {
        this.drawer.classList.remove('pointer-events-auto');
        this.drawer.classList.add('pointer-events-none');
      }
    }, 300);
  }

  async refreshCartDrawer() {
    if (this.isLoading) return;

    this.isLoading = true;

    // Remember if drawer was open
    const wasOpen = this.isOpen;

    try {
      // Fetch the cart drawer HTML with updated cart data
      const response = await fetch('/?section_id=cart-drawer', {
        method: 'GET',
        headers: {
          'Accept': 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Create a temporary container to parse the response
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Find the cart drawer content in the response
      const newDrawer = tempDiv.querySelector('#CartDrawer');
      if (newDrawer) {
        // Store current state classes before replacing content
        const drawerClasses = this.drawer.className;

        // Replace the current drawer content but preserve the outer container
        this.drawer.innerHTML = newDrawer.innerHTML;

        // Restore the drawer classes (including visibility state)
        this.drawer.className = drawerClasses;

        // Re-bind events since we replaced the content
        this.rebindEvents();

        // Initialize progress bar animation
        this.initializeProgressBar();

        // If drawer was open, ensure it stays open
        if (wasOpen) {
          this.ensureDrawerOpen();
        }

      }

      // Update cart count in header
      this.updateCartCount();

    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
      // Fallback: reload the page
      window.location.reload();
    } finally {
      this.isLoading = false;
    }
  }

  ensureDrawerOpen() {
    if (!this.drawer || !this.overlay || !this.panel) return;

    // Ensure initial closed state
    this.overlay.classList.add('opacity-0');
    this.overlay.classList.remove('opacity-100');
    this.panel.style.translate = '100% 0';

    // Enable pointer events
    this.drawer.classList.remove('pointer-events-none');
    this.drawer.classList.add('pointer-events-auto');

    // Force reflow
    void this.panel.offsetHeight;

    // Ensure overlay is visible and panel is slid in
    setTimeout(() => {
      if (this.overlay) {
        this.overlay.classList.remove('opacity-0');
        this.overlay.classList.add('opacity-100');
      }
      if (this.panel) {
        this.panel.style.translate = '0 0';
      }
    }, 10);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    this.isOpen = true;
  }

  rebindEvents() {
    // Re-get elements since they were replaced
    this.overlay = document.getElementById('cart-overlay');
    this.panel = document.getElementById('cart-panel');
    this.container = document.getElementById('CartContainer');
    this.footer = document.getElementById('cart-footer');
    this.closeButton = document.getElementById('close-cart-drawer');

    // Re-bind close events
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Bind cart note updates
    const noteTextarea = this.footer?.querySelector('#CartSpecialInstructions');
    if (noteTextarea) {
      noteTextarea.addEventListener('blur', this.updateCartNote.bind(this));
    }

    // Bind form submission
    const form = this.footer?.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.handleCheckout.bind(this));
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateCartBubble(cart.item_count);
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  async handleItemAdded(eventDetail) {
    // Handle both old format (just product) and new format (with button info)
    const product = eventDetail.product || eventDetail;
    const button = eventDetail.button;
    const textContainer = eventDetail.textContainer;
    const originalText = eventDetail.originalText;

    try {
      await this.refreshCartDrawer();
      await this.open();
      // Small delay to ensure DOM is ready
      setTimeout(() => this.initializeProgressBar(), 50);
      refreshLoyaltyLionCartUI();
    } finally {
      // Reset button state after cart drawer is updated
      if (button && originalText) {
        button.disabled = false;
        if (textContainer) {
          textContainer.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
      }
    }
  }

  async updateCartNote(e) {
    const note = e.target.value;

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ note })
      });
    } catch (error) {
      console.error('Error updating cart note:', error);
    }
  }

  handleCheckout(e) {
    const termsCheckbox = document.getElementById('CartAgree');
    if (termsCheckbox && !termsCheckbox.checked) {
      e.preventDefault();
      alert('Please agree to the terms and conditions');
      return false;
    }
  }

  /**
   * Called when cart:updated fires (e.g. AJAX cart page, line-item changes).
   * Drawer line HTML is refreshed via refreshCartDrawer where needed; keep header count in sync.
   */
  updateCartDisplay(cart) {
    if (!cart || typeof cart.item_count !== 'number') {
      return;
    }
    this.updateCartBubble(cart.item_count);
  }

  updateCartBubble(itemCount) {
    const bubbles = document.querySelectorAll('.cart-link__bubble, [data-cart-count]');
    bubbles.forEach(bubble => {
      bubble.textContent = itemCount;
      bubble.style.display = itemCount > 0 ? 'block' : 'none';
    });
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  initializeProgressBar() {
    const progressBar = this.drawer?.querySelector('.fs-progress-bar');
    if (!progressBar) return;

    const targetValue = parseFloat(progressBar.dataset.targetValue);
    if (isNaN(targetValue)) return;

    const duration = 800; // milliseconds
    const startTime = performance.now();

    function animate(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out function
      const easeOut = 1 - Math.pow(1 - progress, 3);

      progressBar.value = targetValue * easeOut;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  }
}

// Global functions for cart operations
window.updateQuantity = async function(lineKey, newQuantity) {
  if (newQuantity < 0) return;

  // Prevent multiple simultaneous updates
  if (window.cartDrawer && window.cartDrawer.isLoading) {
    return;
  }

  // Show loading state on the specific line item
  const lineItem = document.querySelector(`[data-line-key="${lineKey}"]`);
  if (lineItem) {
    lineItem.classList.add('cart-loading');
  }

  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: parseInt(newQuantity)
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const cart = await response.json();

    // Refresh the cart drawer with updated content
    if (window.cartDrawer) {
      await window.cartDrawer.refreshCartDrawer();
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  } catch (error) {
    console.error('Error updating quantity:', error);

    // Remove loading state on error
    if (lineItem) {
      lineItem.classList.remove('cart-loading');
    }

    // Show error message
    alert('Failed to update cart. Please try again.');
  }
};

window.removeLineItem = async function(lineKey) {
  return updateQuantity(lineKey, 0);
};

// Debounce function for input changes
let quantityUpdateTimeout;
window.debounceQuantityUpdate = function(lineKey, newQuantity) {
  clearTimeout(quantityUpdateTimeout);
  quantityUpdateTimeout = setTimeout(() => {
    updateQuantity(lineKey, newQuantity);
  }, 500); // Wait 500ms after user stops typing
};

// Initialize cart drawer when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  window.cartDrawer = new CartDrawer();
});

// Enhanced add to cart functionality with event delegation
// Register this BEFORE DOMContentLoaded to ensure it runs before other scripts
document.addEventListener('submit', async function(e) {
    // Check if this is an add to cart form
    if (e.target.matches('form[action*="/cart/add"]') ||
        e.target.closest('form[action*="/cart/add"]')) {

      // Stop all event propagation immediately
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const form = e.target.matches('form') ? e.target : e.target.closest('form');
      const submitButton = form.querySelector('[type="submit"]');

      if (!submitButton) return;

      // Find the text container - either inside .stitching div or the button itself
      const stitchingDiv = submitButton.querySelector('.stitching');
      const textContainer = stitchingDiv || submitButton;
      const originalText = textContainer.textContent;

      // Show loading state
      submitButton.disabled = true;
      textContainer.textContent = 'Adding...';

      let response;
      try {
        const formData = new FormData(form);

        // Handle subscription options (both Shopify Selling Plans and ReCharge)

        // Check for Shopify Selling Plans (native subscription system)
        const sellingPlanSelect = form.querySelector('[name="selling_plan"]');
        const purchaseTypeRadios = form.querySelectorAll('[name="purchase_type"]');

        // Check for ReCharge widget container (dynamically loaded)
        const rechargeContainer = form.querySelector('.rc-container-wrapper, .rc_container_wrapper, #rc_container');

        // Look for purchase_type radio in the entire form (ReCharge uses these)
        let purchaseTypeChecked = null;
        purchaseTypeRadios.forEach(radio => {
          if (radio.checked) {
            purchaseTypeChecked = radio.value;
          }
        });

        // Check for ReCharge active block (the widget highlights the active selection)
        // Try multiple selectors as ReCharge may use different class structures
        let activeBlock = rechargeContainer?.querySelector('.rc_block__type--active');
        if (!activeBlock) {
          activeBlock = rechargeContainer?.querySelector('.rc-option--active');
        }
        if (!activeBlock) {
          activeBlock = rechargeContainer?.querySelector('[class*="active"]');
        }

        const isSubscriptionActive = activeBlock?.classList.contains('rc_block__type__autodeliver') ||
                                     activeBlock?.querySelector('[value="autodeliver"]')?.checked ||
                                     activeBlock?.querySelector('[value="subsave"]')?.checked;
        const isOnetimeActive = activeBlock?.classList.contains('rc_block__type__onetime') ||
                               activeBlock?.querySelector('[value="onetime"]')?.checked;

        // Override purchaseTypeChecked based on ReCharge visual state if available
        // The ReCharge widget may not update the radio button's checked state
        if (isSubscriptionActive) {
          purchaseTypeChecked = 'subsave';
        } else if (isOnetimeActive) {
          purchaseTypeChecked = 'onetime';
        }

        // Handle Shopify Selling Plans - Check if subscription is selected
        // The ReCharge widget creates radio buttons for selling plans but we need to manage them correctly
        if (purchaseTypeChecked === 'onetime') {
          // Remove selling_plan from FormData if it exists
          if (formData.has('selling_plan')) {
            formData.delete('selling_plan');
          }
          // Also remove any selling_plan radio button values (ReCharge creates these)
          const allSellingPlanInputs = form.querySelectorAll('[name^="selling_plan"]');
          allSellingPlanInputs.forEach(input => {
            if (formData.has(input.name)) {
              formData.delete(input.name);
            }
          });
        } else if (purchaseTypeChecked === 'autodeliver' || purchaseTypeChecked === 'subsave') {
          // Ensure selling_plan is in the FormData
          if (!formData.has('selling_plan')) {
            // Try to find the selling plan from checked radio buttons
            const sellingPlanRadio = form.querySelector('[name^="selling_plan_"]:checked');
            if (sellingPlanRadio) {
              const planId = sellingPlanRadio.name.replace('selling_plan_', '');
              formData.set('selling_plan', planId);
            } else if (sellingPlanSelect && sellingPlanSelect.value) {
              // Last resort: check if there's a selling_plan select and use its value
              formData.set('selling_plan', sellingPlanSelect.value);
            }
          }

          // Remove the purchase_type from FormData - Shopify doesn't use this
          if (formData.has('purchase_type')) {
            formData.delete('purchase_type');
          }

          // Also remove the selling_plan_* radio button entries (keep only selling_plan)
          const allSellingPlanRadios = form.querySelectorAll('[name^="selling_plan_"]');
          allSellingPlanRadios.forEach(radio => {
            if (formData.has(radio.name)) {
              formData.delete(radio.name);
            }
          });
        }

        // Handle legacy ReCharge subscriptions (for backward compatibility)
        const purchaseTypeAutodeliver = form.querySelector('#rc_purchase_type_autodeliver, input[value="autodeliver"][name="purchase_type"]');

        if (purchaseTypeAutodeliver && purchaseTypeAutodeliver.checked) {
          // User selected subscription option
          const shippingIntervalFrequency = form.querySelector('#rc_shipping_interval_frequency');
          const shippingIntervalUnitType = form.querySelector('#rc_shipping_interval_unit_type');

          if (shippingIntervalFrequency && shippingIntervalFrequency.value) {
            formData.append('properties[shipping_interval_frequency]', shippingIntervalFrequency.value);
          }

          if (shippingIntervalUnitType && shippingIntervalUnitType.value) {
            formData.append('properties[shipping_interval_unit_type]', shippingIntervalUnitType.value);
          }

          // Check if we need to swap to discount variant (if ReCharge is using variant mapping)
          // The ReCharge widget stores variant mappings in window.ReCharge.products[productId].variant_to_duplicate
          const currentVariantId = formData.get('id');

          // Try multiple methods to get the discount variant ID
          let discountVariantId = null;

          // Method 1: Check if ReCharge has a getDiscountVariant method
          if (window.ReCharge && typeof window.ReCharge.getDiscountVariant === 'function') {
            discountVariantId = window.ReCharge.getDiscountVariant(currentVariantId);
          }

          // Method 2: Check ReCharge products mapping (most common approach)
          if (!discountVariantId && window.ReCharge && window.ReCharge.products) {
            const productId = form.dataset.productid || form.querySelector('[data-productid]')?.dataset.productid;
            if (productId && window.ReCharge.products[productId]) {
              const product = window.ReCharge.products[productId];
              if (product.variant_to_duplicate && product.variant_to_duplicate[currentVariantId]) {
                discountVariantId = product.variant_to_duplicate[currentVariantId];
              }
            }
          }

          // Method 3: Check global variant_to_duplicate (fallback)
          if (!discountVariantId && window.ReCharge && window.ReCharge.variant_to_duplicate) {
            discountVariantId = window.ReCharge.variant_to_duplicate[currentVariantId];
          }

          // Apply the discount variant if found
          if (discountVariantId) {
            formData.set('id', discountVariantId);
          }
        }

        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/json'
          },
          body: formData
        }).catch(fetchError => {
          throw new Error('Network request failed: ' + fetchError.message);
        });

        if (!response) {
          throw new Error('No response received from server');
        }

        // Check if response is successful
        if (!response.ok) {
          let errorMessage = 'Failed to add item to cart';

          try {
            const errorData = await response.json();
            errorMessage = errorData.description || errorData.message || errorMessage;
          } catch (parseError) {
            // If we can't parse JSON, use a generic message
            errorMessage = 'Unable to add item to cart';
          }

          throw new Error(errorMessage);
        }

        const product = await response.json();

        // Trigger cart item added event with button reference for cleanup
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: {
            product: product,
            button: submitButton,
            textContainer: textContainer,
            originalText: originalText
          }
        }));

      } catch (error) {
        console.error('Error adding to cart:', error);

        // Show the actual error message or a fallback
        const userMessage = error.message || 'Failed to add item to cart. Please try again.';
        alert(userMessage);

        // Reset button state on error
        submitButton.disabled = false;
        textContainer.textContent = originalText;
      }
    }
  }, true); // Use capture phase to run before other event listeners

// Handle cart icon clicks to open drawer
document.addEventListener('DOMContentLoaded', function() {
  const cartLinks = document.querySelectorAll('.cart-link, .js-drawer-open-cart, [data-cart-drawer-open]');
  cartLinks.forEach(link => {
    link.addEventListener('click', function(e) {
      // Don't open drawer if we're already on the cart page
      if (window.location.pathname === '/cart') {
        return; // Let the link navigate normally
      }

      e.preventDefault();
      if (window.cartDrawer) {
        window.cartDrawer.open();
      }
    });
  });
});
